import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Card, CardContent, Loading } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { ClaimsAdminTopNav } from '@src/components/Claims/ClaimsAdminTopNav';
import { useAuth } from '@src/hooks/useAuth';
import { useClients } from '@src/hooks/useClients';
import { ClaimsTab } from '@src/hooks/usePaymentRequestClaims';
import { pick } from 'lodash';
import { CLIENT_GROUPS, Confirm, DataTable, DELETE_CLIENT_GROUP, Form, SAVE_CLIENT_GROUP, TrackableButton, useAlert, useLocale, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
export const MemberClients = ({ clientId }) => {
    const { isSuperuser } = useAuth();
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { clients } = useClients({
        initialPageSize: 100,
    });
    const { data, error, loading } = useQuery(CLIENT_GROUPS, {
        variables: {
            clientId,
        },
    });
    const clientOptions = useMemo(() => {
        if (!clients) {
            return [];
        }
        return clients.map((client) => ({
            value: client.id.toString(),
            label: client.name,
        }));
    }, [clients]);
    const [saveClientGroup] = useMutation(SAVE_CLIENT_GROUP, {
        refetchQueries: [CLIENT_GROUPS],
    });
    const [deleteClientGroup] = useMutation(DELETE_CLIENT_GROUP, {
        refetchQueries: [CLIENT_GROUPS],
    });
    const [selectedClientGroup, setSelectedClientGroup] = useState(null);
    const [clientGroupToDelete, setClientGroupToDelete] = useState(null);
    const [saveError, setSaveError] = useState('');
    const clientGroups = useMemo(() => data?.clientGroups || [], [data]);
    const Fields = useMemo(() => [
        {
            name: 'memberClientId',
            label: t('Client'),
            type: 'lookup',
            options: clientOptions,
            serializeValue: value => value.toString(),
            deserializeValue: value => parseInt(value, 10),
        },
    ], [t, clientOptions]);
    const handleValidate = (clientGroup) => {
        const errors = {};
        if (!clientGroup.memberClientId) {
            errors.memberClientId = t('Client is required');
        }
        return errors;
    };
    const handleSave = async (clientGroup) => {
        let result;
        try {
            setSaveError('');
            result = await saveClientGroup({
                variables: {
                    input: {
                        ...pick(clientGroup, ['id', 'clientId', 'memberClientId']),
                    },
                },
            });
            if (result.data) {
                if (result.data.saveClientGroup?.validationErrors) {
                    return result.data.saveClientGroup?.validationErrors;
                }
            }
            showAlert(t('Client saved'));
            setSelectedClientGroup(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const msg = e.message;
            setSaveError(t(msg));
        }
    };
    const handleDelete = async () => {
        if (clientGroupToDelete) {
            try {
                await deleteClientGroup({
                    variables: {
                        id: clientGroupToDelete.id,
                    },
                });
                showAlert(t('Client deleted'));
                setClientGroupToDelete(null);
            }
            catch (e) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                showAlert(e.message, 'error');
            }
        }
    };
    return (_jsx(Loading, { label: t('Member Clients'), loaded: !loading, showChildrenWhileLoading: true, overlay: true, children: _jsxs("div", { children: [isSuperuser && (_jsx(ClaimsAdminTopNav, { showFilter: false, tab: ClaimsTab.CONFIG, isSuperuser: isSuperuser, actions: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add', tag: 'clients_list', variant: 'contained', onClick: () => setSelectedClientGroup({
                            id: 0,
                            clientId,
                            memberClientId: 0,
                        }), children: t('Add') }) })), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: clientGroups, pageSizeOptions: [], hideFooter: true, columns: [
                                        {
                                            field: 'memberClient',
                                            headerName: t('Client'),
                                            flex: 0.1,
                                            sortable: false,
                                            renderCell: ({ row }) => row.memberClient?.name || '',
                                        },
                                        {
                                            field: 'action',
                                            headerName: t('Actions'),
                                            renderCell: ({ row }) => (isSuperuser && (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `client_group_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedClientGroup(row), className: 'margin--right_8' }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `client_group_${row.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => setClientGroupToDelete(row), className: 'margin--right_8' })] }))) ||
                                                null,
                                            width: 100,
                                            sortable: false,
                                        },
                                    ] }), selectedClientGroup && (_jsx(Form, { title: selectedClientGroup.id ? 'Edit Client' : 'Add Client', modal: true, obj: selectedClientGroup, fields: Fields, onValidate: handleValidate, onSave: handleSave, onCancel: () => {
                                        setSelectedClientGroup(null);
                                        setSaveError('');
                                    }, error: saveError })), clientGroupToDelete && (_jsx(Confirm, { message: t('Are you sure you want to delete this member client?'), onConfirm: handleDelete, onCancel: () => setClientGroupToDelete(null) }))] }) }) })] }) }));
};
